import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import { Button, Flex, Form, Input, Table, Modal, Pagination, Popconfirm, Typography, message } from "antd";
import { CopyOutlined, DeleteOutlined } from "@ant-design/icons";
import { API, graphqlOperation } from "aws-amplify";
import Service from "../../../services";
import * as mutations from "../../../graphql/mutations";
import { errorToString, getUserInfo } from "../../../utils";
import ScheduleButton from "../../../components/TouchstoneSubmissions/ScheduleButton";
import { SubmissionLogs } from "../../../components/TouchstoneSubmissions/SubmissionLogs";
import { SubmissionSources } from "../../../components/TouchstoneSubmissions/SubmissionSources";
import { SubmissionResults } from "../../../components/TouchstoneSubmissions/SubmissionResults";
import { NewSubmissionButton } from "../../../components/TouchstoneSubmissions/NewSubmissionButton";
import { GSI_PK, TOUCHSTONE_SUBMISSIONS_STATUS, TOUCHSTONE_SYSTEMS, TOUCHSTONE_TASKS_STATUS } from "../../../utils/touchstone";

const { Title } = Typography;

const pageSizes = [20, 50, 100];

const TouchstoneSubmissions = () => {
  const [submissions, setSubmissions] = useState();
  const [userInfo, setUserInfo] = useState();
  const [loading, setLoading] = useState(false);
  const [totalSubmissions, setTotalSubmissions] = useState(999999999);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: pageSizes[0],
  });
  const [tokensByPage, setTokensByPage] = useState({ 1: null });
  const [isDescriptionModalVisible, setIsDescriptionModalVisible] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [shouldFetchData, setShouldFetchData] = useState(false);
  const [descriptionForm] = Form.useForm();

  const fetchPage = async (page, pageSize, tokensByPage) => {
    setLoading(true);
    try {
      const token = tokensByPage[page];
      const result = await Service.listTouchstoneSubmissionsPaginated({
        limit: pageSize,
        nextToken: token,
      });

      if (page > 1 && result.items.length === 0) {
        // we scrolled past the last page
        setTotalSubmissions((page - 1) * pageSize);
      } else {
        // for queued submissions, we load their task data too
        const queuedSubmissions = result.items.filter((submission) => submission.status === TOUCHSTONE_SUBMISSIONS_STATUS.QUEUED);
        const tasksPromises = queuedSubmissions.map((submission) => Service.listTasksForSubmission(submission.submission_id));
        const tasksResults = await Promise.all(tasksPromises);

        setSubmissions(
          result.items.map((s) => {
            const tasks = tasksResults.find((tasks) => tasks.length > 0 && tasks[0].submission_id === s.submission_id) || [];
            return {
              ...s,
              key: s.submission_id,
              tasks: tasks,
            };
          })
        );

        if (result.nextToken) {
          setTokensByPage((prev) => ({ ...prev, [page + 1]: result.nextToken }));
        } else {
          setTotalSubmissions(pageSize * page);
        }
      }
    } catch (error) {
      message.error(errorToString(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const userInfo = await getUserInfo();
      setUserInfo(userInfo);
    })();
  }, []);

  useEffect(() => {
    fetchPage(1, pageSizes[0], { 1: null });
    const timer = setInterval(() => {
      setShouldFetchData(true);
    }, 60000);
    return () => clearInterval(timer);
  }, [userInfo]);

  useEffect(() => {
    if (shouldFetchData) {
      fetchPage(pagination.current, pagination.pageSize, tokensByPage);
      setShouldFetchData(false);
    }
  }, [shouldFetchData, pagination, tokensByPage]);

  const handlePaginationChange = (page, pageSize) => {
    setPagination({ current: page, pageSize });
    setShouldFetchData(true);
  };

  const handleCancelSubmission = async (record) => {
    try {
      const input = {
        system: record.system,
        status: TOUCHSTONE_SUBMISSIONS_STATUS.CANCELING,
        ts_created: record.ts_created,
      };
      // Update submission status to CANCELING
      await API.graphql(
        graphqlOperation(mutations.updateTouchstoneSubmissions, {
          input,
        })
      );
      for (const task of record.tasks) {
        if (
          ![
            TOUCHSTONE_TASKS_STATUS.FINISHED_CANCELED,
            TOUCHSTONE_TASKS_STATUS.FINISHED_ERROR,
            TOUCHSTONE_TASKS_STATUS.FINISHED_SUCCESS,
          ].includes(task.status)
        ) {
          await API.graphql(
            graphqlOperation(mutations.updateTouchstoneTasks, {
              input: {
                submission_id: task.submission_id,
                task_id: task.task_id,
                status: TOUCHSTONE_TASKS_STATUS.PENDING_CANCEL,
                status_timestamp: Date.now() / 1000,
              },
            })
          );
        }
      }
      message.success("Submission canceling initiated.");
      setShouldFetchData(true);
    } catch (error) {
      message.error(`Failed to cancel submission: ${error.toString()}`);
    }
  };

  const handleDelete = async (record) => {
    try {
      await API.graphql(
        graphqlOperation(mutations.deleteTouchstoneSubmissions, {
          input: {
            system: record.system,
            ts_created: record.ts_created,
          },
        })
      );
      setShouldFetchData(true);
      message.success(`Submission deleted successfully.`);
    } catch (error) {
      console.log("Delete Failed:", error);
      message.error(errorToString(error));
    }
  };

  const getClonedSubmissionId = (oldId) => {
    const hexSuffixPattern = /_[0-9a-fA-F]{4}$/;
    const baseId = oldId.replace(hexSuffixPattern, "");
    const randomHex = Math.floor(Math.random() * 0x10000)
      .toString(16)
      .padStart(4, "0");
    return `${baseId}_${randomHex}`;
  };

  const handleDuplicate = async (record) => {
    let response;
    const now = Math.floor(Date.now() / 1000);
    const params = {
      submission_id: getClonedSubmissionId(record.submission_id),
      system: record.system,
      gsi_pk: GSI_PK,
      owner: record.owner,
      description: `${record.description || ""}\n(Cloned from ${record.submission_id} by ${userInfo.username})`,
      sources: record.sources,
      status: TOUCHSTONE_SUBMISSIONS_STATUS.ACCEPTED,
      ts_created: now,
    };

    try {
      response = await API.graphql(
        graphqlOperation(mutations.createTouchstoneSubmissions, {
          input: {
            ...params,
          },
        })
      );
    } catch (error) {
      console.log("Failed to duplicate submission:", error, "   input:", params);
      message.error(errorToString(error));
      return;
    }
    setShouldFetchData(true);
    message.success(`Submission ${response.data.createTouchstoneSubmissions.submission_id} created successfully.`);
  };

  const handleEditDescription = async () => {
    try {
      const values = await descriptionForm.validateFields();
      const input = {
        system: selectedSubmission.system,
        ts_created: selectedSubmission.ts_created,
        description: values.description,
      };

      await API.graphql(
        graphqlOperation(mutations.updateTouchstoneSubmissions, {
          input,
        })
      );
      setIsDescriptionModalVisible(false);
      setSelectedSubmission(null);
      setShouldFetchData(true);
      message.success("Description updated successfully.");
    } catch (error) {
      console.error("Failed to update description:", error);
      message.error(`Failed to update description: ${errorToString(error)}`);
    }
  };

  const handleScheduleSuccess = async (system, ts_created) => {
    const status = TOUCHSTONE_SUBMISSIONS_STATUS.QUEUED;
    const ts_queued = Math.floor(Date.now() / 1000);

    const input = {
      system: system,
      ts_created: ts_created,
      status: status,
      ts_queued: ts_queued,
    };
    try {
      await API.graphql(
        graphqlOperation(mutations.updateTouchstoneSubmissions, {
          input: input,
        })
      );
      setShouldFetchData(true);
    } catch (error) {
      console.error("Failed to update status to 'queued':", error);
      throw error;
    }
  };

  const renderStatus = (status, isTouchstoneAdmin) => {
    const nonAdminStatusMap = {
      [TOUCHSTONE_SUBMISSIONS_STATUS.SUBMITTED]: TOUCHSTONE_SUBMISSIONS_STATUS.SUBMITTED,
      [TOUCHSTONE_SUBMISSIONS_STATUS.VALIDATING]: TOUCHSTONE_SUBMISSIONS_STATUS.SUBMITTED,
      [TOUCHSTONE_SUBMISSIONS_STATUS.VALIDATION_ERROR]: TOUCHSTONE_SUBMISSIONS_STATUS.SUBMITTED,
      [TOUCHSTONE_SUBMISSIONS_STATUS.REJECTED]: TOUCHSTONE_SUBMISSIONS_STATUS.REJECTED,
      [TOUCHSTONE_SUBMISSIONS_STATUS.ACCEPTED]: TOUCHSTONE_SUBMISSIONS_STATUS.ACCEPTED,
      [TOUCHSTONE_SUBMISSIONS_STATUS.QUEUED]: TOUCHSTONE_SUBMISSIONS_STATUS.ACCEPTED,
      [TOUCHSTONE_SUBMISSIONS_STATUS.ERROR]: TOUCHSTONE_SUBMISSIONS_STATUS.ACCEPTED,
      [TOUCHSTONE_SUBMISSIONS_STATUS.SUCCESS]: TOUCHSTONE_SUBMISSIONS_STATUS.ACCEPTED,
      [TOUCHSTONE_SUBMISSIONS_STATUS.CANCELING]: TOUCHSTONE_SUBMISSIONS_STATUS.ACCEPTED,
      [TOUCHSTONE_SUBMISSIONS_STATUS.CANCELED]: TOUCHSTONE_SUBMISSIONS_STATUS.ACCEPTED,
    };
    if (isTouchstoneAdmin) return status;
    return nonAdminStatusMap[status];
  };

  if (!submissions) {
    return null; // let the effect run first
  }

  const cognitoGroups = userInfo.groups;
  const isTouchstoneAdmin = cognitoGroups.includes("touchstone_admin");

  const columns = [];

  columns.push(
    {
      title: "Created At",
      dataIndex: "ts_created",
      key: "ts_created",
      sorter: (a, b) => a["ts_created"] - b["ts_created"],
      defaultSortOrder: "descend",
      render: (ts_created) => dayjs(ts_created * 1000).format("MMM D, YYYY HH:mm"),
    },
    {
      title: "Submission ID",
      dataIndex: "submission_id",
      key: "submission_id",
      sorter: (a, b) => a["submission_id"].localeCompare(b["submission_id"]),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) => {
        const errstat = TOUCHSTONE_SUBMISSIONS_STATUS.ERROR;
        const hasErrors =
          status === TOUCHSTONE_SUBMISSIONS_STATUS.QUEUED &&
          record.tasks.some((task) => [TOUCHSTONE_TASKS_STATUS.ERROR, TOUCHSTONE_TASKS_STATUS.FINISHED_ERROR].includes(task.status));
        const errors = hasErrors ? <div className={errstat}>+ errors</div> : null;
        const displayedStatus = renderStatus(status, isTouchstoneAdmin);
        return (
          <>
            <span className={displayedStatus}>{displayedStatus}</span>
            {errors}
          </>
        );
      },
      sorter: (a, b) => a.status.localeCompare(b.status),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const isDeletable = [
          TOUCHSTONE_SUBMISSIONS_STATUS.ACCEPTED,
          TOUCHSTONE_SUBMISSIONS_STATUS.REJECTED,
          TOUCHSTONE_SUBMISSIONS_STATUS.CANCELED,
        ].includes(record.status);

        const isSchedulable = isTouchstoneAdmin && TOUCHSTONE_SUBMISSIONS_STATUS.ACCEPTED === record.status;

        const isDuplicatable =
          isTouchstoneAdmin &&
          record.system === TOUCHSTONE_SYSTEMS.FEATURE_SET &&
          [
            TOUCHSTONE_SUBMISSIONS_STATUS.ACCEPTED,
            TOUCHSTONE_SUBMISSIONS_STATUS.CANCELED,
            TOUCHSTONE_SUBMISSIONS_STATUS.CANCELING,
            TOUCHSTONE_SUBMISSIONS_STATUS.ERROR,
            TOUCHSTONE_SUBMISSIONS_STATUS.QUEUED,
            TOUCHSTONE_SUBMISSIONS_STATUS.SUCCESS,
          ].includes(record.status);

        return (
          <Flex vertical>
            {record.status === TOUCHSTONE_SUBMISSIONS_STATUS.QUEUED && (
              <Popconfirm title="Are you sure you want to cancel this submission?" onConfirm={() => handleCancelSubmission(record)}>
                <Button type="link" icon={<DeleteOutlined />} danger>
                  Cancel
                </Button>
              </Popconfirm>
            )}
            {isSchedulable && (
              <ScheduleButton submissionRecord={record} onTaskScheduled={() => handleScheduleSuccess(record.system, record.ts_created)} />
            )}
            {isDuplicatable && (
              <Button type="link" icon={<CopyOutlined />} onClick={() => handleDuplicate(record)}>
                Clone
              </Button>
            )}
            {isDeletable && (
              <Popconfirm title="Are you sure to delete this submission?" onConfirm={() => handleDelete(record)}>
                <Button type="link" icon={<DeleteOutlined />} danger>
                  Delete
                </Button>
              </Popconfirm>
            )}
          </Flex>
        );
      },
    },
    Table.EXPAND_COLUMN,
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (description, record) => (
        <span>
          <Button
            type="link"
            onClick={() => {
              setSelectedSubmission(record);
              setIsDescriptionModalVisible(true);
              descriptionForm.setFieldsValue({ description: description || "" });
            }}
            style={{
              padding: 0,
              display: "inline-block",
              color: "grey",
              overflow: "hidden",
            }}
          >
            {description ? description.slice(0, 30) + (description.length > 30 ? "..." : "") : "n/a"}
          </Button>
        </span>
      ),
    },
    {
      title: "Sources",
      dataIndex: "sources",
      key: "sources",
      render: (sources) => <SubmissionSources sources={sources} />,
    }
  );

  if (isTouchstoneAdmin) {
    columns.push(
      {
        title: "Created By",
        dataIndex: "owner",
        key: "owner",
        sorter: (a, b) => a["ts_created"] - b["ts_created"],
        render: (owner) => (owner ? owner.split("::")[1] : "N/A"),
      },
      {
        title: "Queued At",
        dataIndex: "ts_queued",
        key: "ts_queued",
        sorter: (a, b) => a["ts_queued"] - b["ts_queued"],
        render: (ts_queued) => ts_queued && dayjs(ts_queued * 1000).format("MMM D, YYYY HH:mm"),
      },
      {
        title: "Finished At",
        dataIndex: "ts_finished",
        key: "ts_finished",
        sorter: (a, b) => a["ts_finished"] - b["ts_finished"],
        render: (ts_finished) => ts_finished && dayjs(ts_finished * 1000).format("MMM D, YYYY HH:mm"),
      }
    );
  }

  columns.push({
    title: "Logs",
    dataIndex: "console_log_tail",
    key: "console_log_tail",
    render: (console_log_tail) => <SubmissionLogs console_log_tail={console_log_tail} />,
  });

  return (
    <div>
      <NewSubmissionButton userInfo={userInfo} setShouldFetchData={setShouldFetchData} />
      <Modal
        title="Edit Description"
        width={640}
        open={isDescriptionModalVisible}
        onOk={handleEditDescription}
        onCancel={() => {
          setIsDescriptionModalVisible(false);
          setSelectedSubmission(null);
        }}
      >
        <Form form={descriptionForm} layout="vertical">
          <Form.Item name="description" label="Description" rules={[{ required: false, message: "Please enter a description" }]}>
            <Input.TextArea rows={4} placeholder="Enter description" />
          </Form.Item>
        </Form>
      </Modal>

      <Title level={3} style={{ marginTop: 40 }}>
        All Submissions
      </Title>
      <Flex vertical gap={10}>
        <Table
          columns={columns}
          dataSource={submissions}
          style={{ marginTop: 20 }}
          pagination={false}
          loading={loading}
          expandable={{
            rowExpandable: (record) => !!record.results,
            expandedRowRender: (record) => <SubmissionResults results={record.results} />,
          }}
        />
        <Pagination
          onChange={handlePaginationChange}
          current={pagination.current}
          pageSizeOptions={pageSizes}
          defaultPageSize={pageSizes[0]}
          showSizeChanger={true}
          total={totalSubmissions}
        />
      </Flex>
      <style>{`
      .${TOUCHSTONE_SUBMISSIONS_STATUS.SUBMITTED} {
        color: lightslategray;
      }
      .${TOUCHSTONE_SUBMISSIONS_STATUS.REJECTED} {
        color: red;
      }
      .${TOUCHSTONE_SUBMISSIONS_STATUS.ACCEPTED} {
        color: dodgerblue;
      }
      .${TOUCHSTONE_SUBMISSIONS_STATUS.QUEUED} {
        color: plum;
      }
      .${TOUCHSTONE_SUBMISSIONS_STATUS.CANCELING} {
        color: darkorange;
      }
      .${TOUCHSTONE_SUBMISSIONS_STATUS.CANCELED} {
        color: orange;
      }
      .${TOUCHSTONE_SUBMISSIONS_STATUS.ERROR} {
        color: darkred;
      }
      .${TOUCHSTONE_SUBMISSIONS_STATUS.VALIDATION_ERROR} {
        color: darkred;
      }
      .${TOUCHSTONE_SUBMISSIONS_STATUS.SUCCESS} {
        color: green;
      }
      .${TOUCHSTONE_SUBMISSIONS_STATUS.FINISHED_CANCELED} {
        color: crimson;
      }
    `}</style>
    </div>
  );
};

export default TouchstoneSubmissions;
